import { defineStore } from 'pinia';
import { API } from '@/api/api';
import { cookiesMaxAge } from '~/common/const';
import { base64ToObj } from '~/utils/base64';

export interface Session {
  id: null | string;
  depth: number;
  ts: string;
  rhost: string;
  cpc: boolean;
}

export interface Additional {
  split?: string;
  utm_medium?: string | null | undefined;
  utm_source?: string | null | undefined;
  utm_campaign?: string | null | undefined;
  utm_content?: string | null | undefined;
  utm_term?: string | null | undefined;
}

export const useAnalyticsStore = defineStore('analyticsStore', () => {
  const headers = useRequestHeaders();
  const route = useRoute();

  // STATES
  const additional = ref<Additional>({
    split: 'a',
    utm_medium: '',
    utm_source: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
  });
  const oldCookies = ref<Additional>({
    utm_medium: '',
    utm_source: '',
    utm_campaign: '',
    utm_content: '',
    utm_term: '',
  });
  const headersAll = ref({});
  const userIp = ref('');
  const userId = ref('');
  const oldUserId = ref('');
  const isNewSession = ref(false);
  const session = ref<Session>({
    id: null,
    depth: 0,
    ts: '',
    rhost: '',
    cpc: false,
  });
  const cpcshows = ref<{ [key: string]: number }>({});
  const pageviewId = ref('');
  const viewObserve = ref<IntersectionObserver | null>(null);
  const visibleList = ref(new Map());
  const offersClick = ref<{ [key: string]: number }>({});
  const url = useRequestURL();
  const domain = `.${url.hostname.split('.').slice(-2).join('.')}`;
  // COOKIES
  const cookie_utm_medium = useCookie('utm_medium', {
    path: '/',
    domain,
    maxAge: cookiesMaxAge,
  });
  const cookie_utm_source = useCookie('utm_source', {
    path: '/',
    domain,
    maxAge: cookiesMaxAge,
  });
  const cookie_utm_campaign = useCookie('utm_campaign', {
    path: '/',
    domain,
    maxAge: cookiesMaxAge,
  });
  const cookie_utm_content = useCookie('utm_content', {
    path: '/',
    domain,
    maxAge: cookiesMaxAge,
  });
  const cookie_utm_term = useCookie('utm_term', {
    path: '/',
    domain,
    maxAge: cookiesMaxAge,
  });

  // ACTIONS
  function setUserId(value: string) {
    userId.value = value;
  }

  function setOldUserId(value: string) {
    oldUserId.value = value;
  }

  function setSession(value: Session) {
    session.value = value;
  }

  function setAdditional(value: Additional) {
    additional.value = value;
  }

  function setOldCookies(value: Additional) {
    oldCookies.value = value;
  }

  function setPageviewId(value: string) {
    pageviewId.value = value;
  }

  function setDepth() {
    session.value.depth = session.value.depth + 1;
  }

  function setHeadersAll(value: Additional) {
    headersAll.value = value;
  }

  function setViewObserve(value: IntersectionObserver | null) {
    viewObserve.value = value;
  }

  function clearCpcShows() {
    cpcshows.value = {};
  }

  function setOfferClick({
    offerId,
    clicks,
  }: {
    offerId: string;
    clicks: number;
  }) {
    offersClick.value[offerId] = clicks;
  }

  function addOfferClick(offerId: string) {
    if (offersClick.value[offerId]) {
      setOfferClick({
        offerId,
        clicks: ++offersClick.value[offerId],
      });
    } else {
      setOfferClick({ offerId, clicks: 1 });
    }
  }

  function initViewObserve() {
    const delay: number = Math.abs(+(import.meta.env.VITE_VIEW_DELAY || 100));
    setViewObserve(
      new IntersectionObserver(
        (entries) => {
          const remarketingArr: number[] = [];
          entries.forEach((entry: IntersectionObserverEntry) => {
            const block: string =
              (<HTMLElement>entry.target).dataset.block || '';
            const slot: string = (<HTMLElement>entry.target).dataset.slot || '';

            if (entry.isIntersecting) {
              const type: string =
                (<HTMLElement>entry.target).dataset.type || '';
              if (
                type === 'model' &&
                (block === 'listing' || block === 'search-listing')
              )
                remarketingArr.push(
                  +((<HTMLElement>entry.target).dataset.rid || '')
                );
              const urlBase64: string =
                (<HTMLElement>entry.target).dataset.url || '';
              const url: string = urlBase64 ? base64ToObj(urlBase64)?.url : '';
              const id = (<HTMLElement>entry.target).dataset.offer_id;
              // const { $retailRocketProductEvent } = useNuxtApp();
              if (url) {
                if (/\/stats\/ov\?/.test(url)) {
                  cpcshows.value[id] = Date.now();
                }
                visibleList.value.set(
                  block + slot,
                  setTimeout(() => {
                    // if ($retailRocketProductEvent && id) {
                    //   $retailRocketProductEvent(id);
                    // }
                    if (navigator.sendBeacon)
                      navigator.sendBeacon(
                        `/v4${url}&session_id=${session.value.id}&pageview_id=${pageviewId.value}&slot=${slot}&block=${block}`
                      );
                    else
                      API.post(`${url}`, null, {
                        params: {
                          slot,
                          session_id: session.value.id,
                          pageview_id: pageviewId.value,
                          block,
                        },
                      }).catch(() => {});
                    viewObserve.value?.unobserve(entry.target);
                    visibleList.value.delete(block + slot);
                  }, delay)
                );
              }
            } else {
              const timeout = visibleList.value.get(block + slot);
              if (timeout) {
                clearTimeout(timeout);
                visibleList.value.delete(block + slot);
              }
            }
          });
          if (remarketingArr.length && (<Window>window).dataLayer)
            (<Window>window).dataLayer.push({
              ecomm_prodid: remarketingArr,
              ecomm_pagetype:
                (<HTMLElement>entries[0].target).dataset.block === 'listing'
                  ? 'category'
                  : 'searchresults',
            });
        },
        {
          threshold: 0.7,
        }
      )
    );
  }

  function observeView(elem: HTMLElement) {
    if (viewObserve.value && viewObserve.value.observe instanceof Function) {
      viewObserve.value.observe(elem);
    }
  }

  function unobserveView(elem: HTMLElement) {
    if (viewObserve.value && viewObserve.value.observe instanceof Function) {
      viewObserve.value.unobserve(elem);
    }
    const block: string = elem.dataset.block || '';
    const slot: string = elem.dataset.slot || '';
    const timeout = visibleList.value.get(block + slot);
    if (timeout) {
      clearTimeout(timeout);
      visibleList.value.delete(block + slot);
    }
  }

  const compareUtm = (query: { [key: string]: string | string[] }) => {
    for (const [key, value] of Object.entries(query)) {
      if (!Object.prototype.hasOwnProperty.call(oldCookies.value, key)) {
        return false;
      }
      // @ts-ignore
      if (String(value) !== String(oldCookies?.value?.[key])) {
        return true;
      }
    }
    return false;
  };

  function initAnalytics(isClient = false) {
    const split = String(
      route.query._split || headers['x-split'] || additional.value.split
    );

    const utm_medium = String(
      route.query?.utm_medium || cookie_utm_medium.value || ''
    );
    const utm_source = String(
      route.query?.utm_source || cookie_utm_source.value || ''
    );
    const utm_campaign = String(
      route.query?.utm_campaign || cookie_utm_campaign.value || ''
    );
    const utm_content = String(
      route.query?.utm_content || cookie_utm_content.value || ''
    );
    const utm_term = String(
      route.query?.utm_term || cookie_utm_term.value || ''
    );
    setAdditional({
      split,
      utm_medium,
      utm_source,
      utm_campaign,
      utm_content,
      utm_term,
    });
    setOldCookies({
      utm_medium: cookie_utm_medium.value,
      utm_source: cookie_utm_source.value,
      utm_campaign: cookie_utm_campaign.value,
      utm_content: cookie_utm_content.value,
      utm_term: cookie_utm_term.value,
    });

    isNewSession.value = compareUtm(route.query);
    if (
      route.query?.utm_medium &&
      route.query?.utm_medium !== cookie_utm_medium.value
    ) {
      cookie_utm_medium.value = String(route.query.utm_medium);
    }
    if (
      route.query?.utm_source &&
      route.query?.utm_source !== cookie_utm_source.value
    ) {
      cookie_utm_source.value = String(route.query.utm_source);
    }

    if (
      route.query?.utm_campaign &&
      route.query?.utm_campaign !== cookie_utm_campaign.value
    ) {
      cookie_utm_campaign.value = String(route.query.utm_campaign);
    }
    if (
      route.query?.utm_content &&
      route.query?.utm_content !== cookie_utm_content.value
    ) {
      cookie_utm_content.value = String(route.query.utm_content);
    }
    if (
      route.query?.utm_term &&
      route.query?.utm_term !== cookie_utm_term.value
    ) {
      cookie_utm_term.value = String(route.query.utm_term);
    }
    if (!isClient) {
      userIp.value = headers['x-real-ip'] || '';
      if (!oldUserId.value) {
        userId.value =
          headers['x-user'] || '00000000-0000-0000-0000-000000000000';
        setOldUserId(userId.value);
      }

      setHeadersAll(headers);
    }
  }

  return {
    observeView,
    unobserveView,
    additional,
    cpcshows,
    clearCpcShows,
    setAdditional,
    userIp,
    userId,
    session,
    pageviewId,
    setPageviewId,
    viewObserve,
    visibleList,
    offersClick,
    setUserId,
    setSession,
    setDepth,
    initViewObserve,
    addOfferClick,
    initAnalytics,
    isNewSession,
    oldCookies,
    setHeadersAll,
    headersAll,
    oldUserId,
  };
});
